import { skipToken, useQuery } from "@tanstack/react-query";

import { TenantRead, getRanaApiV1 } from "@nens/rana/api-v1";
import { getHCCApi } from "@nens/threedi/openapi-client";

import { useTenantQuery } from "./query-tenant";

function getHccLiveSiteAccessQuery(tenant?: TenantRead) {
  return getRanaApiV1().getQueryOptions({
    // getQueryOptions adds the current Rana tenant to the final query key, so the tenant ID does
    // not need to be repeated here.
    queryKey: ["hcc-live-site-access"],
    queryFn: tenant
      ? async () => {
          // Avoid an unnecessary HCC request for tenants without linked organisations.
          if (tenant.threediOrganisations.length === 0) return false;

          // HCC filters the organisations by both the current tenant's IDs and the required
          // contract scope. One result is enough because access is granted when any organisation
          // has a valid livesite contract.
          const organisations = await getHCCApi().organisationsList({
            contractScopeIn: ["livesite"],
            limit: 1,
            // Rana stores UUIDs with hyphens, while this HCC endpoint expects them without.
            uniqueIdIn: tenant.threediOrganisations.map((organisation) =>
              organisation.replace(/-/g, "")
            ),
            validContractsOnly: true,
          });

          return organisations.results.length > 0;
        }
      : skipToken,
  });
}

export const useHccLiveSiteAccessQuery = () => {
  // The tenant query provides the HCC organisation IDs belonging to the current Rana tenant.
  const tenantQuery = useTenantQuery();
  const tenant = tenantQuery.data;

  const liveSiteAccessQuery = useQuery(getHccLiveSiteAccessQuery(tenant));

  const hasAccess = liveSiteAccessQuery.data === true;

  return {
    isLoading: tenantQuery.isLoading || liveSiteAccessQuery.isLoading,
    hasAccess,
  };
};
